<script setup lang="ts">
import { computed } from 'vue'
import { useTabStore } from '@/stores/tabs.store'
import type { TAB_BUTTON_POSITION, TAB_ICONS_NAMES } from '@/utils/tabs'
import TabsSettings from '@/components/ide/ide/TabsSettings.vue'
import closeIcon from '@/assets/images/ide/closebutton.svg'
import tabsSettingIcon from '@/assets/images/ide/dots-vertical.svg'

let timeoutId: NodeJS.Timeout | null = null

const props = defineProps({
  tabName: {
    type: String,
    required: false
  },
  currentPosition: {
    type: String,
    required: false
  },
  tabDisplayName: {
    type: String,
    required: false
  }
})

const tabStore = useTabStore()

const showTabSettings = computed(() => {
  return (
    tabStore.openTabSettings.visible && tabStore.openTabSettings.position == props.currentPosition
  )
})

/**
 * Opens the tabs settings for a specific tab.
 * @param tabPosition - defines which tab is on hover
 */
const handleMouseEnter = (tabPosition: string | undefined) => {
  if (timeoutId) {
    clearTimeout(timeoutId)
    timeoutId = null
  }
  if (tabPosition) {
    tabStore.setTabSettings({ visible: true, position: tabPosition })
  }
}

/**
 * Hide menu on mouse leave after short delay
 *   @param tabPosition - defines which tab is on hover
 */
const handleMouseLeave = (tabPosition: string | undefined) => {
  timeoutId = setTimeout(() => {
    if (tabPosition) {
      tabStore.setTabSettings({
        visible: !tabStore.openTabSettings.visible,
        position: tabPosition
      })
    }
  }, 250) // 250ms second delay
}

/**
 * close the current active tab
 */
const handleCloseTab = () => {
  tabStore.setActiveTabPosition(
    props.currentPosition as TAB_BUTTON_POSITION,
    props.tabName as TAB_ICONS_NAMES
  )
}
</script>

<template>
  <div class="flex flex-row justify-center">
    <div class="text-high m-auto min-w-0 truncate pr-5 text-xs font-normal tracking-tight">
      {{ props.tabDisplayName }}
    </div>
    <div class="flex items-center pr-0.5">
      <button
        @mouseenter="handleMouseEnter(props.currentPosition)"
        @mouseleave="handleMouseLeave(props.currentPosition)"
      >
        <img :src="tabsSettingIcon" class="h-4.5 w-4.5" />
      </button>
    </div>
    <div class="mt-0.5">
      <button type="button" class="hs-tooltip-toggle h-3 w-3" @click="handleCloseTab()">
        <img :src="closeIcon" />
      </button>
    </div>
  </div>

  <div v-if="showTabSettings" class="fixed z-40">
    <TabsSettings
      :tabName="props.tabName"
      :currentPosition="props.currentPosition"
      @mouseenter="handleMouseEnter(props.currentPosition)"
      @mouseleave="handleMouseLeave(props.currentPosition)"
    />
  </div>
</template>
